import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  ArrowLeft, Calendar, Clock, Loader2, Plus, Trash2, Edit2,
  Image as ImageIcon, Video, Send, X, Check, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScheduledPostsPage() {
  const { token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [postsRes, statsRes] = await Promise.all([
        axios.get(`${API}/posts/scheduled?include_published=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/posts/scheduled/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setScheduledPosts(postsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch scheduled posts:', error);
      toast.error('Failed to load scheduled posts');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (postId) => {
    if (!confirm('Are you sure you want to delete this scheduled post?')) return;
    
    try {
      await axios.delete(`${API}/posts/scheduled/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scheduled post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleCancel = async (postId) => {
    try {
      await axios.post(`${API}/posts/scheduled/${postId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scheduled post cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel post');
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'text-blue-500 bg-blue-500/10';
      case 'published': return 'text-green-500 bg-green-500/10';
      case 'cancelled': return 'text-gray-500 bg-gray-500/10';
      case 'failed': return 'text-red-500 bg-red-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-20" data-testid="scheduled-posts-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Calendar className="w-6 h-6 text-primary" />
              <h1 className="font-bold text-lg">Scheduled Posts</h1>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="rounded-full"
            data-testid="create-scheduled-post-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Schedule
          </Button>
        </div>
      </header>

      <div className="p-4 space-y-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-3xl font-bold text-blue-500">{stats.scheduled}</p>
                <p className="text-sm text-muted-foreground">Scheduled</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-3xl font-bold text-green-500">{stats.published}</p>
                <p className="text-sm text-muted-foreground">Published</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-3xl font-bold text-gray-500">{stats.cancelled}</p>
                <p className="text-sm text-muted-foreground">Cancelled</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-3xl font-bold text-red-500">{stats.failed}</p>
                <p className="text-sm text-muted-foreground">Failed</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Next Scheduled Post */}
        {stats?.next_scheduled && (
          <Card className="bg-gradient-to-r from-primary/10 to-violet-500/10 border-primary/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Next scheduled post</span>
              </div>
              <p className="text-lg font-semibold">{formatDateTime(stats.next_scheduled.scheduled_time)}</p>
              <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                {stats.next_scheduled.content || 'Media post'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Scheduled Posts List */}
        <section>
          <h2 className="font-semibold mb-4">All Scheduled Posts</h2>
          
          {scheduledPosts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground">No scheduled posts yet</p>
                <p className="text-sm text-muted-foreground">Create your first scheduled post!</p>
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Post
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {scheduledPosts.map(post => (
                <Card key={post.id} data-testid={`scheduled-post-${post.id}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-4">
                      {/* Media Preview */}
                      {post.media && (
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-secondary shrink-0">
                          {post.media_type === 'video' ? (
                            <div className="w-full h-full flex items-center justify-center bg-black/50">
                              <Video className="w-8 h-8 text-white" />
                            </div>
                          ) : (
                            <img src={post.media} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(post.status)}`}>
                            {post.status}
                          </span>
                          {post.status === 'failed' && (
                            <span className="text-xs text-red-500" title={post.error_message}>
                              <AlertCircle className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm line-clamp-2">{post.content || 'Media post'}</p>
                        
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDateTime(post.scheduled_time)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      {post.status === 'scheduled' && (
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-full"
                            onClick={() => setEditingPost(post)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-full text-red-500 hover:text-red-600"
                            onClick={() => handleCancel(post.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {(post.status === 'cancelled' || post.status === 'failed') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-full text-red-500 hover:text-red-600"
                          onClick={() => handleDelete(post.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingPost) && (
        <SchedulePostModal
          editPost={editingPost}
          onClose={() => { setShowCreateModal(false); setEditingPost(null); }}
          onSuccess={() => { setShowCreateModal(false); setEditingPost(null); fetchData(); }}
        />
      )}
    </div>
  );
}

// Schedule Post Modal Component
function SchedulePostModal({ editPost, onClose, onSuccess }) {
  const { token } = useAuth();
  const [content, setContent] = useState(editPost?.content || '');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [media, setMedia] = useState(editPost?.media || null);
  const [mediaType, setMediaType] = useState(editPost?.media_type || null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (editPost?.scheduled_time) {
      const dt = new Date(editPost.scheduled_time);
      setScheduledDate(dt.toISOString().split('T')[0]);
      setScheduledTime(dt.toTimeString().slice(0, 5));
    } else {
      // Default to 1 hour from now
      const future = new Date(Date.now() + 3600000);
      setScheduledDate(future.toISOString().split('T')[0]);
      setScheduledTime(future.toTimeString().slice(0, 5));
    }
  }, [editPost]);

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/media`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setMedia(response.data.url);
      setMediaType(response.data.media_type);
      toast.success('Media uploaded');
    } catch (error) {
      toast.error('Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!content && !media) {
      toast.error('Please add content or media');
      return;
    }

    if (!scheduledDate || !scheduledTime) {
      toast.error('Please select date and time');
      return;
    }

    const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();

    setLoading(true);
    try {
      if (editPost) {
        await axios.put(`${API}/posts/scheduled/${editPost.id}`, {
          content,
          scheduled_time: scheduledDateTime,
          media,
          media_type: mediaType
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Scheduled post updated');
      } else {
        await axios.post(`${API}/posts/scheduled`, {
          content,
          scheduled_time: scheduledDateTime,
          media,
          media_type: mediaType
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post scheduled!');
      }
      onSuccess();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to schedule post';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="schedule-post-modal">
        <DialogHeader>
          <DialogTitle>{editPost ? 'Edit Scheduled Post' : 'Schedule a Post'}</DialogTitle>
          <DialogDescription>
            Your post will be automatically published at the scheduled time
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Content */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Content</label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What's on your mind?"
              rows={4}
              data-testid="scheduled-content-input"
            />
          </div>

          {/* Media Upload */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Media (optional)</label>
            {media ? (
              <div className="relative w-full h-40 rounded-lg overflow-hidden bg-secondary">
                {mediaType === 'video' ? (
                  <video src={media} className="w-full h-full object-cover" />
                ) : (
                  <img src={media} alt="" className="w-full h-full object-cover" />
                )}
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 rounded-full"
                  onClick={() => { setMedia(null); setMediaType(null); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <label className="block w-full p-8 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors text-center">
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleMediaUpload}
                  className="hidden"
                  disabled={uploading}
                />
                {uploading ? (
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                ) : (
                  <>
                    <ImageIcon className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Click to upload image or video</p>
                  </>
                )}
              </label>
            )}
          </div>

          {/* Schedule Date/Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Date</label>
              <Input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="scheduled-date-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Time</label>
              <Input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                data-testid="scheduled-time-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading || uploading}>
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {editPost ? 'Update' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
